import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Mail, Phone, MapPin, Cake, Heart, Globe, Download, User } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SharedContact {
  firstName: string
  lastName?: string | null
  nickname?: string | null
  email?: string | null
  phone?: string | null
  company?: string | null
  jobTitle?: string | null
  location?: string | null
  birthday?: string | null
  anniversary?: string | null
  notes?: string | null
  avatarUrl?: string | null
  links?: Record<string, string> | null
}

function getInitials(firstName: string, lastName?: string | null): string {
  return `${firstName[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
}

export default function SharedContactPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['shared-contact', token],
    queryFn: async () => {
      const res = await fetch(`/api/shared/${token}`)
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message || 'Not found')
      }
      return res.json() as Promise<{ contact: SharedContact; includedFields: string[] }>
    },
    enabled: !!token,
  })

  if (isLoading) {
    return (
      <div className="bg-background flex h-dvh items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-background flex h-dvh flex-col items-center justify-center p-6 text-center">
        <User className="text-on-surface-variant/50 mb-4 size-12" />
        <h1 className="mb-2 text-xl font-semibold">Link Unavailable</h1>
        <p className="text-on-surface-variant text-sm">
          This share link may have expired or been revoked.
        </p>
      </div>
    )
  }

  const { contact } = data
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  return (
    <div className="bg-background min-h-dvh">
      <div className="mx-auto max-w-md p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          {contact.avatarUrl ? (
            <img
              src={contact.avatarUrl}
              alt={fullName}
              className="ring-primary/10 size-24 rounded-full object-cover ring-4"
            />
          ) : (
            <div className="bg-primary/10 text-primary ring-primary/5 flex size-24 items-center justify-center rounded-full text-3xl font-semibold ring-4">
              {getInitials(contact.firstName, contact.lastName)}
            </div>
          )}
          <div>
            <h1 className="font-heading text-2xl font-bold">{fullName}</h1>
            {contact.nickname && (
              <p className="text-on-surface-variant text-sm">"{contact.nickname}"</p>
            )}
            {(contact.jobTitle || contact.company) && (
              <p className="text-on-surface-variant mt-0.5 text-sm">
                {[contact.jobTitle, contact.company].filter(Boolean).join(' at ')}
              </p>
            )}
          </div>
        </div>

        {/* Contact info */}
        <div className="mb-6 space-y-3">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="bg-surface-container-high hover:bg-surface-container-highest flex items-center gap-3 rounded-xl p-3 transition-colors"
            >
              <Mail className="text-primary size-5" />
              <span className="text-sm">{contact.email}</span>
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="bg-surface-container-high hover:bg-surface-container-highest flex items-center gap-3 rounded-xl p-3 transition-colors"
            >
              <Phone className="text-primary size-5" />
              <span className="text-sm">{contact.phone}</span>
            </a>
          )}
          {contact.location && (
            <div className="bg-surface-container-high flex items-center gap-3 rounded-xl p-3">
              <MapPin className="text-primary size-5" />
              <span className="text-sm">{contact.location}</span>
            </div>
          )}
          {contact.birthday && (
            <div className="bg-surface-container-high flex items-center gap-3 rounded-xl p-3">
              <Cake className="text-primary size-5" />
              <span className="text-sm">{contact.birthday}</span>
            </div>
          )}
          {contact.anniversary && (
            <div className="bg-surface-container-high flex items-center gap-3 rounded-xl p-3">
              <Heart className="text-primary size-5" />
              <span className="text-sm">{contact.anniversary}</span>
            </div>
          )}
        </div>

        {/* Social links */}
        {contact.links && Object.keys(contact.links).length > 0 && (
          <div className="mb-6">
            <h3 className="text-on-surface-variant mb-2 text-sm font-medium">Links</h3>
            <div className="space-y-2">
              {Object.entries(contact.links).map(([label, url]) =>
                url ? (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-surface-container-high hover:bg-surface-container-highest flex items-center gap-3 rounded-xl p-3 transition-colors"
                  >
                    <Globe className="text-primary size-5" />
                    <span className="text-sm capitalize">{label}</span>
                  </a>
                ) : null,
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {contact.notes && (
          <div className="mb-6">
            <h3 className="text-on-surface-variant mb-2 text-sm font-medium">Notes</h3>
            <div className="bg-surface-container-high rounded-xl p-3 text-sm whitespace-pre-wrap">
              {contact.notes}
            </div>
          </div>
        )}

        {/* Download vCard */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => window.open(`/api/shared/${token}/vcard`, '_blank')}
        >
          <Download className="mr-1.5 size-4" />
          Save to Contacts
        </Button>

        {/* Branding */}
        <p className="text-on-surface-variant/50 mt-6 text-center text-xs">Shared via Aime</p>
      </div>
    </div>
  )
}
